import './styles.scss';

import { LostAndFoundHistoryRow, useLostAndFoundHistory } from '@/features/LostAndFoundHistory';

import { LostAndFoundHistoryFormDefinitions } from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryForm/types';
import LostAndFoundHistoryTableColumns from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryTable/LostAndFoundHistoryTableColumns';
import LostAndFoundHistoryTableRow from '@/features/LostAndFoundHistory/components/LostAndFoundHistoryTable/LostAndFoundHistoryTableRow';
import SimpleTable from '@/components/SimpleTable';
import { SortDirectionEnum } from '@/models/gen/graphql';
import { generateSearchableString } from '@/utils';

const LostAndFoundHistoryTable = (): JSX.Element => {
  const [{ reports: sortedReports }] = useLostAndFoundHistory(({ reports, sortBy, sortDirection, search = '' }) => ({
    reports: Array.from(reports.keys())
      .filter((id: string) => {
        if ((search ?? '') === '') return true;
        const accident = reports.get(id);
        if (!accident) return false;
        const searchable = generateSearchableString(accident, LostAndFoundHistoryFormDefinitions.searchable);
        return searchable.includes(search?.toLowerCase?.());
      })
      .sort((a: string, b: string): number => {
        if (!sortDirection || !sortBy) return 0;
        const sortReports = compareReportsBy(sortBy);
        const result = sortReports(reports.get(a), reports.get(b));
        if (sortDirection === SortDirectionEnum.Desc) return -result;
        return result;
      }),
  }));

  return (
    <SimpleTable
      className="LostAndFoundHistoryTable"
      rows={sortedReports}
      columns={LostAndFoundHistoryTableColumns}
      row={LostAndFoundHistoryTableRow}
    />
  );
};

const compareReportsBy =
  (sortBy: string) =>
  (reportA: LostAndFoundHistoryRow, reportB: LostAndFoundHistoryRow): number => {
    if (!reportA || !reportB) return 0;
    switch (sortBy) {
      case 'TRIP_AIRPORT_CODE':
        if (!reportA.tripId || !reportB.tripId) {
          const airportCodeA = reportA.crewAirportCode || reportA.tripAirportCode;
          const airportCodeB = reportB.crewAirportCode || reportB.tripAirportCode;
          return airportCodeA.localeCompare(airportCodeB);
        }
        return reportA.tripAirportCode.localeCompare(reportB.tripAirportCode);
      case 'CREATED_AT':
        return new Date(reportA.createdAt).getTime() - new Date(reportB.createdAt).getTime();
      case 'DISPATCHER_NAME':
        return [reportA.dispatcherFirstName, reportA.dispatcherLastName]
          .join(' ')
          .localeCompare([reportB.dispatcherFirstName, reportB.dispatcherLastName].join(' '));
      case 'TRIP_SERVICER_IATA_AIRLINE_CODE':
        return reportA.tripServicerIataAirlineCode.localeCompare(reportB.tripServicerIataAirlineCode);
      case 'TRIP_FLIGHT_NAME':
        let flightNumberOrLoopNameA = reportA.tripLoopName || reportA.tripFlightNumber.toString();
        let flightNumberOrLoopNameB = reportB.tripLoopName || reportB.tripFlightNumber.toString();
        if (!reportA.tripId || !reportB.tripId) {
          flightNumberOrLoopNameA = reportA.crewFlightName || flightNumberOrLoopNameA;
          flightNumberOrLoopNameB = reportB.crewFlightName || flightNumberOrLoopNameB;
        }
        return flightNumberOrLoopNameA.localeCompare(flightNumberOrLoopNameB);
      case 'VEHICLE_TRACKING_ID':
        return reportA.vehicleTrackingId.localeCompare(reportB.vehicleTrackingId);
      case 'DRIVER_NAME':
        return [reportA.driverFirstName, reportA.driverLastName]
          .join(' ')
          .localeCompare([reportB.driverFirstName, reportB.driverLastName].join(' '));
      case 'DESCRIPTION':
        return reportA.description.localeCompare(reportB.description);
      case 'RETURN_STATUS':
        return reportA.returnStatus.localeCompare(reportB.returnStatus);
      default:
        return 0;
    }
  };

export default LostAndFoundHistoryTable;
